import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box, Button, Chip, FormControl, Grid, IconButton, InputAdornment,
  InputLabel, MenuItem, Select, Stack,
  TablePagination, TextField, Typography,
} from "@mui/material";
import { useState } from "react";
import { EmptyState, FilterBarBox, LoadingBlock, PALETTE, filterSelectSx } from "../components/shared";
import CourseCard from "../components/CourseCard";
import { useCourses } from "../hooks/useCourses";

type Props = { onOpenCourse: (courseId: string) => void; onBookmarkChange?: () => void };

export default function CoursesView({ onOpenCourse, onBookmarkChange }: Props) {
  const cp = useCourses();
  const [draft, setDraft] = useState(cp.filters);

  const apply = (next: typeof draft) => { setDraft(next); cp.applyFilters(next); };
  const clear = () => apply({ search: "", category: "", language: "", difficulty: "" });
  const hasFilters = Object.values(draft).some(Boolean);

  return (
    <Box sx={{ fontSize: 13, maxWidth: 1320, mx: "auto" }}>
      <FilterBarBox sx={{ flexWrap: "wrap", gap: 1, py: 1.25, mb: 1.25 }}>
        <FilterListIcon sx={{ fontSize: 15, color: "primary.main", flexShrink: 0 }} />
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "primary.main", whiteSpace: "nowrap" }}>Filters</Typography>
        <TextField
          size="small"
          placeholder="Search courses..."
          value={draft.search}
          onChange={(e) => setDraft((p) => ({ ...p, search: e.target.value }))}
          onKeyDown={(e) => e.key === "Enter" && apply(draft)}
          slotProps={{
            input: {
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 15, color: "text.disabled" }} /></InputAdornment>,
              endAdornment: draft.search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => apply({ ...draft, search: "" })}>
                    <ClearIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
              sx: { fontSize: 12, borderRadius: "8px", bgcolor: "#fff" },
            },
          }}
          sx={{ flex: "1 1 160px", minWidth: 140, maxWidth: 240, "& .MuiOutlinedInput-notchedOutline": { borderColor: PALETTE.borderDark } }}
        />

        {[["category", "Category", cp.facets.categories ?? []], ["language", "Language", cp.facets.programmingLanguages ?? cp.facets.languages ?? []], ["difficulty", "Difficulty", ["beginner", "intermediate", "advanced"]]].map(([key, label, opts]: any) => (
          <FormControl size="small" sx={{ flex: "0 1 120px", minWidth: 100 }} key={key}>
            <InputLabel sx={{ fontSize: 12 }}>{label}</InputLabel>
            <Select
              label={label}
              value={(draft as any)[key]}
              onChange={(e) => apply({ ...draft, [key]: e.target.value })}
              sx={{ ...filterSelectSx, fontSize: 12 }}
              MenuProps={{ PaperProps: { style: { maxHeight: 220 } } }}
            >
              <MenuItem value="" sx={{ fontSize: 12 }}>All</MenuItem>
              {opts.map((o: string) => (
                <MenuItem key={o} value={o} sx={{ fontSize: 12, textTransform: "capitalize" }}>{o}</MenuItem>
              ))}
            </Select>
          </FormControl>
        ))}

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: { xs: 0, md: "auto" }, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: 12, color: PALETTE.textSecondary, whiteSpace: "nowrap" }}>{cp.total} courses</Typography>
          {hasFilters && (
            <Chip label="Clear" size="small" onDelete={clear} onClick={clear}
              sx={{ fontSize: 11, bgcolor: "#fff0f0", color: "#c62828", borderColor: "#ffcdd2" }} variant="outlined" />
          )}
          <Button size="small" variant="contained" onClick={() => apply(draft)}
            sx={{ textTransform: "none", fontSize: 12, height: 30, borderRadius: "8px" }}>
            Search
          </Button>
        </Box>
      </FilterBarBox>

      {cp.loading ? (
        <LoadingBlock message="Loading courses..." />
      ) : cp.courses.length === 0 ? (
        <EmptyState message="No courses found. Try adjusting filters." />
      ) : (
        <>
          <Grid container spacing={1.5}>
            {cp.courses.map((course: any) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={course.id} sx={{ display: "flex" }}>
                <CourseCard
                  course={course}
                  onOpen={() => onOpenCourse(course.id)}
                  onEnroll={() => cp.enroll(course.id)}
                  onToggleBookmark={async () => {
                    try {
                      await cp.toggleBookmark(course.id);
                      onBookmarkChange?.();
                    } catch {
                      // optimistic state is reverted in hook
                    }
                  }}
                />
              </Grid>
            ))}
          </Grid>
          <TablePagination
            component="div"
            count={cp.total}
            page={cp.page}
            rowsPerPage={12}
            rowsPerPageOptions={[12]}
            onPageChange={(_e, p) => cp.changePage(p)}
            sx={{
              mt: 1.25,
              borderRadius: "12px",
              border: `1px solid ${PALETTE.border}`,
              bgcolor: PALETTE.bgLight,
              "& .MuiTablePagination-toolbar": { minHeight: 40 },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontSize: 12 },
            }}
          />
        </>
      )}
    </Box>
  );
}
